import { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";

import { widgetHoverAtom } from "atoms/interaction";
import { EvaChart } from "constants/charts.enums";
import { useChartsContext } from "contexts/ChartContext";
import { isRigFracValueSeries } from "entities/charts/eva/translators/rigFracActivity";
import { translateToOptions } from "entities/charts/translators";
import { useAtomValue } from "jotai";
import { updateChart } from "store/features/charts/chartSlice";
import getChartScreenshotOptions from "utils/chart/getChartScreenshotOptions";

import { IChartScreenshotSettings } from "models/chart";

import { useUserSettings } from "components/user/hooks";

import { isPointerOverChart } from "./echart/tooltipVisibility";
import { AxisKey, blankAxisNames, useHideAxisTitles } from "./echart/useHideAxisTitles";
import { useChartEntities } from "./useChartEntities";
import { useRigFracSeriesColors } from "./useRigFracSeriesColors";

/**
 * Builds the ECharts options for both Rig/Frac charts (EVA-76) and pushes them to
 * the instance — the lean equivalent of the wells `useChartOptions`. Translates the
 * API response by the active chart type, and in screenshot mode runs the SHARED
 * `getChartScreenshotOptions` so rig/frac gets the same screenshot-panel features as
 * every other chart (font sizes, title/axis text overrides, grid-line toggles, font
 * family/weight). `getChartScreenshotOptions` skips its grid / nameGap / spacing
 * overrides for the rig/frac chart type, so the translator's fixed grid + size-aware
 * rotated-label nameGap survive. The LIVE axis options are persisted (never the
 * screenshot-transformed ones) so the AxisContainer/AxisLocker reads the true config.
 */
export function useRigFracChartOptions(
  chartInstanceRef: React.RefObject<Nullable<echarts.ECharts>>,
  isInstanceReady: boolean,
  hoverLegendIdRef?: React.MutableRefObject<string>,
  seriesIdsRef?: React.MutableRefObject<string[]>
) {
  const { id } = useChartsContext();
  const dispatch = useDispatch();
  const {
    apiResponse,
    screenshot,
    chartType,
    locks,
    request,
    dataTable,
    options: persistedOptions
  } = useChartEntities(id);
  // No-op (frozen empty result) for a chart type this hook already only fires
  // for the rig/frac ones, but kept keyed off the same chartType/request read
  // above rather than a second useChartEntities call.
  // The labels this chart will paint, so one the value domain doesn't list is
  // still coloured from the same palette instead of keeping the server's. Same
  // list the legend passes, filtered the same way, or the two would disagree.
  const seriesLabels = (apiResponse?.series ?? [])
    .filter(isRigFracValueSeries)
    .map((s) => s.label);
  const { colors: seriesColors } = useRigFracSeriesColors({
    chartType,
    request,
    seriesLabels
  });
  const { userAbbreviations } = useUserSettings();
  const abbreviations = userAbbreviations?.abbreviations ?? [];
  const prevOptionsRef = useRef<string>("");

  const isScreenshotActive = screenshot?.active ?? false;
  const preset = screenshot?.properties?.preset;

  // The metric buttons (and Activity's time-scale button on the x-axis) overlay
  // these titles on hover; hide the titles while the buttons show (same
  // condition as RigFracMetricSelector's `visible`).
  const isHover = useAtomValue(widgetHoverAtom)?.[id] ?? false;
  const hideTitles = isHover && !isScreenshotActive && !dataTable?.active;
  const titleAxes: AxisKey[] =
    chartType === EvaChart.RigFracRecentActivity ? ["xAxis"] : ["xAxis", "yAxis"];
  useHideAxisTitles(
    chartInstanceRef,
    hideTitles,
    titleAxes,
    persistedOptions,
    isScreenshotActive
  );
  // Re-run on ANY screenshot-settings change while actively capturing (the
  // panel writes the whole preset: title/axis text overrides, font sizes, font
  // family, grid-line + axis toggles, dimensions, …) — mirroring the wells
  // useChartScreenshotOptions, whose effect depends on the entire preset.
  // Keying on width×height alone left every other panel control dead until an
  // unrelated re-render happened to re-run the transform (EVA-76 QA). The
  // abbreviation dictionary is folded in too — it's consumed by the transform
  // (Use Abbreviations) but lives outside the preset, so a mid-capture edit to
  // it must also re-run. Toggling capture off flips the signature and
  // re-renders the plain live layout.
  const screenshotSignature = isScreenshotActive
    ? `true:${JSON.stringify(preset ?? {})}:${JSON.stringify(abbreviations)}`
    : "false";

  useEffect(() => {
    const instance = chartInstanceRef.current;
    if (!instance || instance.isDisposed()) return;

    if (!apiResponse) {
      if (seriesIdsRef) seriesIdsRef.current = [];
      instance.setOption({}, true);
      return;
    }

    // Translate by the ACTIVE chart type. Pass locks so the Activity y-axes clip
    // to the user's lock (Recent has none). request is passed so the Activity
    // translator can tell whether the right metric is a price (EVA-149) — the
    // API response alone carries no metric-type signal.
    const liveOptions = translateToOptions({
      type: chartType,
      states: {
        ...apiResponse,
        chartType,
        locks,
        seriesColors,
        request,
        hoverLegendIdRef
      }
    });

    // Screenshot mode: run the shared pipeline (font sizes, title/axis text
    // overrides, grid-line toggles, hidden dataZoom, uppercase/legacy titles via
    // the preset's titleText). getChartScreenshotOptions SCALES the rig/frac
    // fixed grid + nameGaps with the chosen font sizes (instead of the wells
    // spacing pass) and applies abbreviations to the rig/frac group names
    // (series names + category-axis data) via its own isRigFrac branch — the
    // wells applyAbbreviationsOnXAxis flag stays false here.
    const renderOptions =
      isScreenshotActive && preset
        ? getChartScreenshotOptions(
            liveOptions,
            preset as IChartScreenshotSettings,
            instance,
            abbreviations,
            false,
            undefined,
            apiResponse,
            false,
            undefined
          )
        : hideTitles
        ? blankAxisNames(liveOptions, titleAxes)
        : liveOptions;

    // A notMerge rebuild re-applies the translator's tooltip (show:true),
    // re-sticking a tooltip that RigFracChart's DOM mouseleave handler just
    // removed with show:false — honour the shared pointer-over flag instead
    // (EVA-6690; same guard the wells useChartTooltips applies). Cloned, not
    // mutated: the Activity translator passes a module-shared tooltip object.
    if (renderOptions.tooltip && typeof renderOptions.tooltip === "object") {
      renderOptions.tooltip = {
        ...renderOptions.tooltip,
        show: isPointerOverChart(id)
      };
    }

    // Series index -> id for RigFracChart's hover events, so they never need
    // getOption() (a deep clone of the whole option).
    if (seriesIdsRef) {
      seriesIdsRef.current = ((renderOptions.series ?? []) as { id?: string }[]).map(
        (s) => s.id ?? ""
      );
    }

    instance.setOption(renderOptions, { notMerge: true, lazyUpdate: true });
    instance.resize();

    // Persist the LIVE axis options (never the screenshot-transformed ones) so the
    // AxisContainer/AxisLocker (Activity) reads the true y-axis lock config. Guard
    // against redundant dispatches to avoid churn.
    const optionsJson = JSON.stringify({
      xAxis: liveOptions.xAxis,
      yAxis: liveOptions.yAxis
    });
    if (optionsJson !== prevOptionsRef.current) {
      prevOptionsRef.current = optionsJson;
      dispatch(updateChart({ id, options: optionsJson }));
    }
    // `request` is in the deps because the effect READS it (:85) to decide
    // isPrice. It was previously omitted, which worked only because a
    // secondaryMetric change always produces a new React Query key and therefore a
    // new apiResponse reference — an implicit coupling nothing in this file
    // stated.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    apiResponse,
    isInstanceReady,
    chartType,
    locks,
    request,
    screenshotSignature,
    seriesColors
  ]);
}
